import { CustomerPortalUser } from "../../Models/CustomerPortalUser";
import { PortalValidationResult } from "../../Models/PortalValidationResult";
import { Reference } from "../../Models/Reference";
import { StipDocument } from "../../Models/StipDocument";
import { Stipulation } from "../../Models/Stipulation";
import { StipulationCategory } from "../../Models/StipulationCategory";
import { SharedAction } from "../Shared/actions";
import { CREATE_REFERENCE_SUCCESS, CustomerPortalAction, DELETE_REFERENCE_SUCCESS, FAILED_LOGIN, GET_STIPULATIONS_SUCCESS, GET_STIPULATION_CATEGORIES_SUCCESS, GET_STIPULATION_DOCUMENTS_SUCCESS, GET_USER, GET_USER_SUCCESS, LOAD_REFERENCES_SUCCESS, PORTAL_VALIDATION_ERROR, REMOVE_SCHEDULING_APPT_LOADER, UPDATE_STIP_DOCUMENT_SUCCESS, SCHEDULE_APPOINTMENT, SCHEDULE_APPOINTMENT_SUCCESS, UPDATE_REFERENCE_SUCCESS, UPLOAD_STIP_DOCUMENTS, ADD_STIP_DOCUMENT_TO_DICTIONARY, ADD_STIP_DOCUMENTS_TO_DICTIONARY, SET_USER, UPDATE_PREFERS_SPANISH_SUCCESS, GET_PORTAL_PRE_QUALIFICATION_RESULT, GET_PORTAL_PRE_QUALIFICATION_RESULT_SUCCESS, SET_IS_GETTING_PORTAL_PRE_QUALIFICATION_RESULT } from "./actions";
import { Reducer, combineReducers } from 'redux';

export interface CustomerPortalState {
  portalValidationResult: PortalValidationResult; // result of validating the portal link
  isValidatingPortalLink: boolean; // flag to indicate if the portal link is being validated
  isSchedulingAppointment: boolean; // flag to indicate if the user is scheduling an appointment
  user: CustomerPortalUser; // user information for the customer portal
  stipulationCategories: StipulationCategory[]; // stipulation categories for the customer portal
  stipulations: Stipulation[]; // stipulations for the customer portal
  stipulationDocuments: StipDocument[]; // stipulation documents for the customer portal
  references: Reference[]; // references for the customer portal
  stipDocumentUrlDictionary: { [key: string]: string; }; // dictionary of stipulation URLs. so that we don't refetch images multiple times
  isLoadingPortalPrequalificationResults: boolean;
}

const INITIAL_STATE: CustomerPortalState = {
  isValidatingPortalLink: true,
  isSchedulingAppointment: false,
  portalValidationResult: {},
  user: {
    customerPortalLinkId: '',
    applicationId: '',
    applicationCustomers: [],
    numberOfFailedLoginAttempts: 0,
    assignedEmployeeName: '',
    assignedEmployeeStorePhone: '',
    assignedEmployeeStoreName: '',
    assignedEmployeeAvailabilityStartTime: 8, // default to 8am
    assignedEmployeeAvailabilityEndTime: 18, // default to 6pm
    minimumNumberOfReferences: 0,
    prefersSpanish: false,
    portalStepItems: [],
  },
  stipulationCategories: [],
  stipulations: [],
  stipulationDocuments: [],
  references: [],
  stipDocumentUrlDictionary: {},
  isLoadingPortalPrequalificationResults: false,
};

const isValidatingPortalLinkReducer = (state: boolean = INITIAL_STATE.isValidatingPortalLink, action: CustomerPortalAction): boolean => {
  switch (action.type) {
    case GET_USER:
      return true;
    case GET_USER_SUCCESS:
      return false;
    case PORTAL_VALIDATION_ERROR:
      return false;
    default:
      return state;
  }
}

const isSchedulingAppointmentReducer = (state: boolean = INITIAL_STATE.isSchedulingAppointment, action: CustomerPortalAction): boolean => {
  switch (action.type) {
    case SCHEDULE_APPOINTMENT:
      return true;
    case SCHEDULE_APPOINTMENT_SUCCESS:
      return false;
    case REMOVE_SCHEDULING_APPT_LOADER:
      return false;
    default:
      return state;
  }
}

const portalValidationResultReducer = (state: PortalValidationResult = INITIAL_STATE.portalValidationResult, action: CustomerPortalAction): PortalValidationResult => {
  switch (action.type) {
    case GET_USER_SUCCESS:
      return { ...action.payload.portalValidationResult };
    case PORTAL_VALIDATION_ERROR:
      return { ...action.payload.portalValidationResult };
    default:
      return state;
  }
}

const userReducer = (state: CustomerPortalUser = INITIAL_STATE.user, action: CustomerPortalAction): CustomerPortalUser => {
  switch (action.type) {
    case GET_USER_SUCCESS:
      return { ...action.payload.user, customerPortalLinkId: action.payload.user.customerPortalLinkId };
    case SET_USER:
      return { ...action.payload.user, customerPortalLinkId: action.payload.user.customerPortalLinkId };
    case UPDATE_PREFERS_SPANISH_SUCCESS:
      return { ...state, prefersSpanish: action.payload.prefersSpanish };
    case FAILED_LOGIN:
      return { ...state, numberOfFailedLoginAttempts: state.numberOfFailedLoginAttempts + 1 };
    case SCHEDULE_APPOINTMENT_SUCCESS:
      return { ...state, appointmentDate: action.payload.appointmentDate };
    default:
      return state;
  }
}

const stipulationCategoriesReducer = (state: StipulationCategory[] = INITIAL_STATE.stipulationCategories, action: CustomerPortalAction): StipulationCategory[] => {
  switch (action.type) {
    case GET_STIPULATION_CATEGORIES_SUCCESS:
      return action.payload.stipulationCategories;
    default:
      return state;
  }
}

const stipulationsReducer = (state: Stipulation[] = INITIAL_STATE.stipulations, action: CustomerPortalAction): Stipulation[] => {
  switch (action.type) {
    case GET_STIPULATIONS_SUCCESS:
      return action.payload.stipulations;
    default:
      return state;
  }
}

const stipulationDocumentsReducer = (state: StipDocument[] = INITIAL_STATE.stipulationDocuments, action: CustomerPortalAction): StipDocument[] => {
  switch (action.type) {
    case GET_STIPULATION_DOCUMENTS_SUCCESS:
      return action.payload.stipulationDocuments;
    case UPDATE_STIP_DOCUMENT_SUCCESS:
      return state.map((stipDocument: StipDocument) => {
        if (stipDocument.id === action.payload.stipDocumentId) {
          return { ...stipDocument, name: action.payload.newDocumentName, stipulationId: action.payload.stipulationId };
        }
        return stipDocument;
      });
    case UPLOAD_STIP_DOCUMENTS:
      return [...state, ...action.payload.stipulationDocuments];
    default:
      return state;
  }
}

const referencesReducer = (state: Reference[] = INITIAL_STATE.references, action: CustomerPortalAction): Reference[] => {
  switch (action.type) {
    case LOAD_REFERENCES_SUCCESS:
      return action.payload.references;
    case CREATE_REFERENCE_SUCCESS:
      return [...state, action.payload.reference];
    case UPDATE_REFERENCE_SUCCESS:
      return state.map((reference: Reference) => {
        if (reference.id === action.payload.reference.id) {
          return action.payload.reference;
        }
        return reference;
      });
    case DELETE_REFERENCE_SUCCESS:
      return state.filter((reference: Reference) => reference.id !== action.payload.referenceId);
    default:
      return state;
  }
}

const stipDocumentUrlDictionaryReducer = (state: { [key: string]: string; } = INITIAL_STATE.stipDocumentUrlDictionary, action: CustomerPortalAction): { [key: string]: string; } => {
  switch (action.type) {
    case ADD_STIP_DOCUMENT_TO_DICTIONARY:
      return { ...state, [action.payload.stipDocumentId]: action.payload.objectUrl };
    case ADD_STIP_DOCUMENTS_TO_DICTIONARY:
      // add the new stipulation documents to the dictionary, do not add duplicates
      const newStipDocumentUrlDictionary = { ...state };
      action.payload.items.forEach((item: { stipDocumentId: string, objectUrl: string }) => {
        if (!newStipDocumentUrlDictionary[item.stipDocumentId]) {
          newStipDocumentUrlDictionary[item.stipDocumentId] = item.objectUrl;
        }
      });
      return newStipDocumentUrlDictionary;
    default:
      return state;
  }
}

const isLoadingPortalPrequalificationResultsReducer = (state: boolean = INITIAL_STATE.isLoadingPortalPrequalificationResults, action: CustomerPortalAction | SharedAction): boolean => {
  switch (action.type) {
    case GET_PORTAL_PRE_QUALIFICATION_RESULT:
      return true;
    case GET_PORTAL_PRE_QUALIFICATION_RESULT_SUCCESS:
      return false;
    case SET_IS_GETTING_PORTAL_PRE_QUALIFICATION_RESULT:
      return action.payload.isGettingPreQualificationResult;
    default:
      return state;
  }
}

export const customerPortalReducer = combineReducers({
  isValidatingPortalLink: isValidatingPortalLinkReducer,
  isSchedulingAppointment: isSchedulingAppointmentReducer,
  portalValidationResult: portalValidationResultReducer,
  user: userReducer,
  stipulationCategories: stipulationCategoriesReducer,
  stipulations: stipulationsReducer,
  stipulationDocuments: stipulationDocumentsReducer,
  references: referencesReducer,
  stipDocumentUrlDictionary: stipDocumentUrlDictionaryReducer,
  isLoadingPortalPrequalificationResults: isLoadingPortalPrequalificationResultsReducer,
});
